import { Button } from '@mui/material';
import React, { FC } from 'react';

import {
  ComposeNodeId,
  ComposeNodeType,
} from '../../../../containers/compose/props';

import useComposeType from '../../../../hooks/router/search/useComposeType';
import translate from '../../../../i18n/translate';
import { DialogId } from '../../../../models/dialog';
import ConfirmPopover from '../../../MUI/ConfirmPopover';

import { getCreateButtonLabel } from './helpers';
import * as Styled from './styled';

type Props = {
  formId: string;
  isDraftChanged: boolean;
  isPending: boolean;
  nodeId: ComposeNodeId;
  nodeType: ComposeNodeType;
  requestClear?: () => void;
};

/**
 * Compose modal footer with clear and submit buttons
 *
 * @param props                Props passed to the component
 * @param props.formId         Id of the form used to connect with submit button
 * @param props.isDraftChanged Whether the draft has been changed
 * @param props.isPending      Whether the mutation is in progress
 * @param props.nodeId         Whether the compose form is in template edit mode
 * @param props.nodeType       Whether the compose form is being used for messages or templates
 * @param props.requestClear   Request to clear the compose form
 *
 * @returns                    The component itself
 */
const ComposeModalFooter: FC<Props> = ({
  formId,
  isDraftChanged,
  isPending,
  nodeId,
  nodeType,
  requestClear,
}) => {
  const composeType = useComposeType();

  const createButtonLabel = getCreateButtonLabel(composeType, nodeId, nodeType);
  const shouldShowPopover = requestClear !== undefined;

  return (
    <Styled.Footer>
      {shouldShowPopover && (
        <ConfirmPopover
          onConfirm={requestClear}
          popoverProps={{
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'top',
            },
            transformOrigin: {
              horizontal: 'right',
              vertical: 'bottom',
            },
          }}
          trigger={
            <Button disabled={!isDraftChanged}>
              {translate('GENERAL__CLEAR_FORM')}
            </Button>
          }
          type={DialogId.ComposeClear}
        />
      )}
      <Button
        color="tertiary"
        form={formId}
        loading={isPending}
        type="submit"
        variant="contained"
      >
        {createButtonLabel}
      </Button>
    </Styled.Footer>
  );
};

export default ComposeModalFooter;
